// REGULAR-season start and end dates by sport. Admin-defined via the
// Season Dates panel (season_dates table); the client reads those via
// the useSeasonDates() hook and stores in the react-query cache. When
// admin hasn't set a row for a given (sport, year), the helpers fall
// back to the hardcoded approximations below.
import { queryClient } from './queryClient'

// Read admin-defined season dates from the react-query cache. Returns
// null if the primer hook hasn't run yet OR admin hasn't defined dates
// for the given sport + year combination. Callers should fall back to
// hardcoded defaults when this returns null.
function getAdminSeasonDates(sportKey, year) {
  const rows = queryClient.getQueryData(['season-dates'])
  if (!Array.isArray(rows) || !rows.length) return null
  // Prefer the row matching the current calendar year. If none exists for
  // this year (rare — offseason before admin backfills next year), fall
  // back to the most-recent row for this sport.
  const forSport = rows.filter((r) => r.sport_key === sportKey)
  if (!forSport.length) return null
  const exact = forSport.find((r) => r.season_year === year)
  if (exact) return exact
  // Some sports span calendar years (NFL regular season ends in the year
  // AFTER it began). If a row exists whose regular_season_ends_at is in
  // the current or next year, prefer that.
  const now = new Date()
  const upcoming = forSport
    .filter((r) => r.regular_season_ends_at && new Date(r.regular_season_ends_at) >= now)
    .sort((a, b) => new Date(a.regular_season_ends_at) - new Date(b.regular_season_ends_at))
  if (upcoming.length) return upcoming[0]
  // Last resort — most recent row we have for this sport.
  return forSport.sort((a, b) => b.season_year - a.season_year)[0]
}

export function getSeasonEndDate(sportKey) {
  const year = new Date().getFullYear()
  const dates = {
    basketball_nba: `${year}-04-12`,
    // Last day of the NFL REGULAR season = the Monday after Week 18's
    // Sunday slate. Was Jan 5, which is the day after Week 17 — every
    // full-season NFL league was ending four days before Week 18 was
    // played. Admin-defined season_dates rows override this.
    americanfootball_nfl: `${year + 1}-01-11`,
    baseball_mlb: `${year}-09-29`,
    basketball_ncaab: `${year}-03-08`,
    basketball_wncaab: `${year}-03-08`,
    americanfootball_ufl: `${year}-06-15`,
    americanfootball_ncaaf: `${year}-12-07`,
    // The 2026 WNBA regular season's last games were Sep 24 (verified against
    // the games table). This read Sep 14, ten days early — and because the
    // roll-forward below turns any already-past date into NEXT year, a league
    // created Sep 16 for "Remainder of Regular Season" was given an end date
    // of 2027-09-14. See the Taste the Rainbow league: it ran past the season
    // it was scoped to and could not end. A stale date here is not a small
    // error, it is a one-year error.
    basketball_wnba: `${year}-09-24`,
    icehockey_nhl: `${year}-04-18`,
    soccer_usa_mls: `${year}-10-18`,
  }
  const endDate = dates[sportKey] || `${year}-12-31`
  if (new Date(endDate) < new Date()) {
    const d = new Date(endDate)
    d.setFullYear(d.getFullYear() + 1)
    return d.toISOString().split('T')[0]
  }
  return endDate
}

export function getSeasonStartDate(sportKey) {
  const year = new Date().getFullYear()
  const dates = {
    // 2026-27 opened Oct 20. Note there is no roll-forward guard here, unlike
    // getSeasonEndDate — this returns the date as-is, so an NBA season that
    // opens earlier in a future year needs this value moved.
    basketball_nba: `${year}-10-20`,
    americanfootball_nfl: `${year}-09-04`,
    baseball_mlb: `${year}-03-27`,
    basketball_ncaab: `${year}-11-03`,
    basketball_wncaab: `${year}-11-03`,
    americanfootball_ufl: `${year}-03-28`,
    americanfootball_ncaaf: `${year}-08-23`,
    basketball_wnba: `${year}-05-16`,
    icehockey_nhl: `${year}-10-07`,
    soccer_usa_mls: `${year}-02-22`,
  }
  return dates[sportKey] || null
}

export function isSeasonUnderway(sportKey) {
  if (!sportKey || sportKey === 'all') return false
  const now = new Date()
  const admin = getAdminSeasonDates(sportKey, now.getFullYear())
  // Admin-defined dates take precedence when both boundaries are set.
  if (admin?.regular_season_starts_at && admin?.regular_season_ends_at) {
    return new Date(admin.regular_season_starts_at) <= now
      && now <= new Date(admin.regular_season_ends_at)
  }
  // Hardcoded fallback for pre-admin-populated years.
  const start = getSeasonStartDate(sportKey)
  const end = getSeasonEndDate(sportKey)
  if (!start || !end) return false
  return new Date(start) <= now && now <= new Date(end)
}

// Approximate playoff/postseason end dates by sport. These are when the
// championship round wraps up (NBA Finals, World Series, Stanley Cup, etc.)
// — updated yearly. Returned in the same year-rollover style as
// getSeasonEndDate: if the date is already past, roll forward a year so
// "this year's playoffs" always points to the next live postseason.
export function getPlayoffEndDate(sportKey) {
  const year = new Date().getFullYear()
  const dates = {
    basketball_nba: `${year}-06-22`,
    basketball_wnba: `${year}-10-20`,
    baseball_mlb: `${year}-11-05`,
    icehockey_nhl: `${year}-06-18`,
    americanfootball_nfl: `${year + 1}-02-09`,
    americanfootball_ufl: `${year}-06-15`,
  }
  const endDate = dates[sportKey]
  if (!endDate) return null
  if (new Date(endDate) < new Date()) {
    const d = new Date(endDate)
    d.setFullYear(d.getFullYear() + 1)
    return d.toISOString().split('T')[0]
  }
  return endDate
}

// True only between the regular-season end and the playoff end — i.e.,
// playoffs are happening right now. During the regular season returns
// false; after the championship round returns false. Prior version had
// a bug where it returned true for the entire offseason (only checked
// now <= playoffEnd, not that now >= regular_season_ends_at). The admin-
// defined path here has both boundaries so it's naturally correct; the
// hardcoded fallback path also now includes the missing check.
export function arePlayoffsUnderway(sportKey) {
  if (!sportKey || sportKey === 'all') return false
  if (isSeasonUnderway(sportKey)) return false
  const now = new Date()
  const admin = getAdminSeasonDates(sportKey, now.getFullYear())
  if (admin?.regular_season_ends_at && admin?.playoff_ends_at) {
    return now > new Date(admin.regular_season_ends_at)
      && now <= new Date(admin.playoff_ends_at)
  }
  // Hardcoded fallback — check both endpoints so we don't false-positive
  // during the offseason.
  const seasonEnd = getSeasonEndDate(sportKey)
  const playoffEnd = getPlayoffEndDate(sportKey)
  if (!seasonEnd || !playoffEnd) return false
  return now > new Date(seasonEnd) && now <= new Date(playoffEnd)
}

// End date used by Full Season DFS/contest leagues. During the regular
// season this is the regular-season end (consistent with the legacy
// behavior). During playoffs we extend through the championship round so
// "Full Season" picked in mid-May means "through the Finals", not "ended
// last month". Falls back to regular-season end for sports without a
// configured playoff date.
export function getFullSeasonLeagueEndDate(sportKey) {
  if (arePlayoffsUnderway(sportKey)) {
    const playoffEnd = getPlayoffEndDate(sportKey)
    if (playoffEnd) return playoffEnd
  }
  return getSeasonEndDate(sportKey)
}

// Sport-specific copy for the playoff button label and helper text.
// Used when arePlayoffsUnderway returns true, so the Full Season option
// reads as "Through the Finals" / "Runs through the NBA Finals." rather
// than the generic regular-season language.
export function getPlayoffsButtonLabel(sportKey) {
  return ({
    basketball_nba: 'Through the Finals',
    basketball_wnba: 'Through the Finals',
    baseball_mlb: 'Through the World Series',
    icehockey_nhl: 'Through the Cup Finals',
    americanfootball_nfl: 'Through the Super Bowl',
  })[sportKey] || 'Through Postseason'
}

export function getPlayoffsHelperText(sportKey) {
  return ({
    basketball_nba: 'Runs through the NBA Finals.',
    basketball_wnba: 'Runs through the WNBA Finals.',
    baseball_mlb: 'Runs through the World Series.',
    icehockey_nhl: 'Runs through the Stanley Cup Finals.',
    americanfootball_nfl: 'Runs through the Super Bowl.',
  })[sportKey] || 'Runs through end of postseason.'
}

/**
 * End of the current NFL week — used by single-week contest leagues
 * (sacks / ints / tackles / receptions / td_pass). Returns the upcoming
 * Tuesday 09:00 in the user's local time, which sits comfortably after
 * Monday Night Football wraps. Already-Tuesday-morning bumps to next
 * Tuesday so a Tuesday-create still includes the full week ahead.
 */
export function getNflWeekEnd() {
  const d = new Date()
  const day = d.getDay() // Sun=0, Mon=1, Tue=2, ...
  // Days until next Tuesday 09:00. If it's already Tuesday past 9am we
  // jump to the next Tuesday; before 9am Tuesday counts as still in
  // this week (catches a Tuesday-morning makeup game).
  let daysUntilTuesday = (2 - day + 7) % 7
  if (daysUntilTuesday === 0 && d.getHours() >= 9) daysUntilTuesday = 7
  d.setDate(d.getDate() + daysUntilTuesday)
  d.setHours(9, 0, 0, 0)
  return d.toISOString()
}
